import React, { useState, useEffect, useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Select } from "../../../../../../../../../components/Form";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { CompanyConfig } from "../../../../../../../../../models/company.config";
import { Currency } from "../../../../../../../../../models/currency";
import { Measurement } from "../../../../../../../../../models/measurement";
import { CompanyProfile, OptionsSwitch, UserTypes } from "../../../../../../../../../util/enums";
import Wrapper from "../Wrapper";
import AppGalleryPermissionTooltip from "./AppGalleryPermissionTooltip";
import useActions from "./hooks/useActions";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

const DefaultPreferences = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser, setCurrentUser, isMovingVersion } = useAppState();

  const {
    mutate,
    setIsDefaultValue,
    isLoading,
    isUpdating,
    company,
    dimensionList,
    currencyList,
    weightList,
    hasDefaultValue,
    disableAutoHardwareBox,
    refetch,
  } = useActions();

  const [currencyIdSelected, setCurrencyIdSelected] = useState<number>();
  const [dimensionIdSelected, setDimensionIdSelected] = useState<number>();
  const [weightSelected, setWeightSelected] = useState<number>();
  const [addSignatureForEveryReportPageSelected, setAddSignatureForEveryReportPageSelected] =
    useState<string>("");
  const [autoApproveSelected, setAutoApproveSelected] = useState<string>("");
  const [autoHardwareBox, setAutoHardwareBox] = useState<string>("");
  const [usePhotoGalleryAccessApp, setUsePhotoGalleryAccessApp] = useState<string>("");
  const [allowBookingSearch, setAllowBookingSearch] = useState<string>("");
  const [requiredVolumeConfirmation, setRequiredVolumeConfirmation] = useState<string>("");
  const [seaTrackingEnabled, setSeaTrackingEnabled] = useState<string>("");
  const [airTrackingEnabled, setAirTrackingEnabled] = useState<string>("");

  const updateCurrencyLocalStorage = (currency?: Currency) => {
    if (currentUser)
      setCurrentUser({
        ...currentUser,
        company: {
          ...currentUser.company,
          currency,
          currencyId: currency?.id,
        },
      });
  };

  const updateMeasurementLocalStorage = (id?: number, dimension?: Measurement) => {
    if (currentUser && dimensionIdSelected)
      setCurrentUser({
        ...currentUser,
        company: {
          ...currentUser.company,
          measurement: dimension,
          measurementId: id,
        },
      });
  };

  const updateMeasurementWeightLocalStorage = (id?: number, weight?: Measurement) => {
    if (currentUser && weightSelected) {
      setCurrentUser({
        ...currentUser,
        company: {
          ...currentUser.company,
          measurementWeightId: id,
          measurementWeight: weight,
        },
      });
    }
  };

  const handleUpdateCurrencyLocalStorage = () => {
    const currencyToLocalStorage = currencyList?.find(
      (currency) => currency.id === currencyIdSelected
    );

    updateCurrencyLocalStorage(currencyToLocalStorage);
  };

  const handleUpdateMeasurementLocalStorage = () => {
    const measurementToLocalStorage = dimensionList.find(
      (dimension) => dimension.id === dimensionIdSelected
    );

    updateMeasurementLocalStorage(dimensionIdSelected, measurementToLocalStorage);
  };

  const handleUpdateMeasurementWeightLocalStorage = () => {
    const measurementWeightToLocalStorage = weightList.find(
      (weight) => weight.id === weightSelected
    );

    updateMeasurementWeightLocalStorage(weightSelected, measurementWeightToLocalStorage);
  };

  const handleUpdateAutoApproveLocalStorage = () => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        company: {
          ...currentUser.company,
          autoApproveQuotes: autoApproveSelected === OptionsSwitch.YES,
        },
      });
    }
  };

  const onSave = () => {
    handleUpdateCurrencyLocalStorage();
    handleUpdateMeasurementLocalStorage();
    handleUpdateMeasurementWeightLocalStorage();
    handleUpdateAutoApproveLocalStorage();

    if (company?.id)
      mutate({
        id: company?.id,
        company: {
          ...company,
          currencyId: currencyIdSelected,
          measurementId: dimensionIdSelected,
          measurementWeightId: weightSelected,
          autoApproveQuotes: autoApproveSelected === OptionsSwitch.YES,
          autoHardwareBox: autoHardwareBox === OptionsSwitch.YES,
          allowBookingSearch: allowBookingSearch === OptionsSwitch.YES,
          configs: {
            ...(company?.configs || {}),
            usePhotoGalleryAccessApp: usePhotoGalleryAccessApp === OptionsSwitch.YES,
            isReportSignatureEveryPageEnabled:
              addSignatureForEveryReportPageSelected === OptionsSwitch.YES,
          } as CompanyConfig,
          requiredVolumeConfirmation: requiredVolumeConfirmation === OptionsSwitch.YES,
          seaTrackingEnabled: seaTrackingEnabled === OptionsSwitch.YES,
          airTrackingEnabled: airTrackingEnabled === OptionsSwitch.YES,
        },
      });
  };

  useEffect(() => {
    if (company && hasDefaultValue) {
      setDimensionIdSelected(company?.measurementId);
      setWeightSelected(company?.measurementWeightId);
      setCurrencyIdSelected(company.currencyId || currentUser?.currency?.id);
      setAddSignatureForEveryReportPageSelected(
        company?.configs?.isReportSignatureEveryPageEnabled ? OptionsSwitch.YES : OptionsSwitch.NO
      );
      setAutoApproveSelected(company?.autoApproveQuotes ? OptionsSwitch.YES : OptionsSwitch.NO);
      setAutoHardwareBox(company?.autoHardwareBox ? OptionsSwitch.YES : OptionsSwitch.NO);
      setUsePhotoGalleryAccessApp(
        company?.configs?.usePhotoGalleryAccessApp ? OptionsSwitch.YES : OptionsSwitch.NO
      );
      setAllowBookingSearch(company?.allowBookingSearch ? OptionsSwitch.YES : OptionsSwitch.NO);
      setRequiredVolumeConfirmation(
        company?.requiredVolumeConfirmation ? OptionsSwitch.YES : OptionsSwitch.NO
      );
      setSeaTrackingEnabled(company?.seaTrackingEnabled ? OptionsSwitch.YES : OptionsSwitch.NO);
      setAirTrackingEnabled(company?.airTrackingEnabled ? OptionsSwitch.YES : OptionsSwitch.NO);
    }
  }, [company, hasDefaultValue]);

  const showBookingsSection = useMemo(
    () =>
      currentUser?.typeUser === UserTypes.ADMIN &&
      currentUser?.company?.profile?.initials === CompanyProfile.SPP,
    [currentUser]
  );

  return (
    <Wrapper
      loading={isUpdating}
      actionButtonProps={{
        dataCyCancel: "preference-cancel-button",
        cancelDisabled: isLoading,
        onCancel: () => {
          setIsDefaultValue(true);
          refetch();
        },
        saveDisabled: (permission && !isShowAction(currentUser, permission?.edit)) || isLoading,
        dataCySave: "preference-button-save",
        onSave,
      }}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item md={6} lg={6}>
          <SkeletonComponent isLoading={isLoading}>
            <Select
              dataCy="currency"
              label={translate("currency")}
              name="currency"
              value={currencyIdSelected}
              onChange={(event) => {
                setIsDefaultValue(false);
                setCurrencyIdSelected(Number(event.target.value));
              }}
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {currencyList?.map((currency) => (
                <MenuItem data-cy={currency.id} key={currency.id} value={currency.id}>
                  {currency.code} - {currency.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
      </Grid>
      <section className={classes.section}>
        <p className={classes.sectionTitle}>{translate("measurements")}</p>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <SkeletonComponent isLoading={isLoading}>
              <Select
                dataCy="dimensions"
                label={translate("dimensions")}
                name="dimensions"
                value={dimensionIdSelected}
                onChange={(event) => {
                  setIsDefaultValue(false);
                  setDimensionIdSelected(Number(event.target.value));
                }}
                displayEmpty
                placeholder={translate("selectAnOption")}
              >
                {dimensionList.map((dimension) => (
                  <MenuItem key={dimension.id} data-cy={dimension.code} value={dimension.id}>
                    {dimension.description} {`${dimension.code ? ` - ${dimension.code}` : ""}`}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>

          <Grid item md={6} lg={6}>
            <SkeletonComponent isLoading={isLoading}>
              <Select
                dataCy="weight"
                label={translate("weight")}
                name="weight"
                value={weightSelected}
                onChange={(event) => {
                  setIsDefaultValue(false);
                  setWeightSelected(Number(event.target.value));
                }}
                displayEmpty
                placeholder={translate("selectAnOption")}
              >
                {weightList.map((weight) => (
                  <MenuItem key={weight.id} data-cy={weight.code} value={weight.id}>
                    {weight.description} {`${weight.code ? ` - ${weight.code}` : ""}`}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>
        </Grid>
      </section>

      {showBookingsSection && (
        <section className={classes.section}>
          <p className={classes.sectionTitle}>{translate("bookings")}</p>
          <Grid container direction="row" alignItems="flex-start" spacing={2}>
            <Grid item md={3} lg={3}>
              <SkeletonComponent isLoading={isLoading}>
                <Select
                  dataCy="allow-booking-search"
                  label={translate("allowBookingSearch")}
                  name="allowBookingSearch"
                  value={allowBookingSearch}
                  onChange={(event: SelectChangeEvent<any>) => {
                    setIsDefaultValue(false);
                    setAllowBookingSearch(event.target.value);
                  }}
                  displayEmpty
                  placeholder={translate("selectAnOption")}
                >
                  <MenuItem
                    key={"allowBookingSearch-yes"}
                    data-cy={"yes"}
                    value={OptionsSwitch.YES}
                  >
                    {translate("yes")}
                  </MenuItem>
                  <MenuItem key={"allowBookingSearch-no"} data-cy={"no"} value={OptionsSwitch.NO}>
                    {translate("no")}
                  </MenuItem>
                </Select>
              </SkeletonComponent>
            </Grid>
          </Grid>
        </section>
      )}

      {isMovingVersion && (
        <>
          <section className={classes.section}>
            <p className={classes.sectionTitle}>{translate("reports")}</p>
            <Grid container direction="row" alignItems="flex-start">
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("addSignatureForEveryPage")}
                    dataCy="reportAddSignatureForEveryPage"
                    name="reportAddSignatureForEveryPage"
                    value={addSignatureForEveryReportPageSelected}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setAddSignatureForEveryReportPageSelected(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                    infoMessage={translate(
                      "addSignatureForOwnerAndLeaderForEveryPageOfReportsInsteadOfTheLastOne"
                    )}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
            </Grid>
          </section>
          <section className={classes.section}>
            <p className={classes.sectionTitle}>{translate("quoteApproval")}</p>
            <Grid container direction="row" alignItems="flex-start">
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("automaticallyApprove")}
                    dataCy="approveQuote"
                    name="approveQuote"
                    value={autoApproveSelected}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setAutoApproveSelected(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                    infoMessage={translate(
                      "whenTheUserApprovesHisQuoteThisWillAutomaticallyUpdateTheQuoteOnTheSystem"
                    )}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
            </Grid>
          </section>
          <section className={classes.section}>
            <p className={classes.sectionTitle}>{translate("inventory")}</p>
            <Grid container direction="row" alignItems="flex-start">
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("automaticallyCreateOneHardwareBoxInEveryInventory")}
                    dataCy="automaticHardwareBox"
                    name="automaticHardwareBox"
                    value={autoHardwareBox}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setAutoHardwareBox(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                    disabled={disableAutoHardwareBox}
                    infoMessage={
                      disableAutoHardwareBox
                        ? translate("needsAtLeastOneActiveHardwareBoxItemToBeEnabled")
                        : undefined
                    }
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
            </Grid>
          </section>
          <section className={classes.section}>
            <p className={classes.sectionTitle}>{translate("apps")}</p>
            <Grid container direction="row" alignItems="flex-start" spacing={2}>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={`${translate("allowItemPhotosToBeUploadedFromTheDevicesImageGallery")}?`}
                    dataCy="allowuseDeviceGallery"
                    name="allowuseDeviceGallery"
                    value={usePhotoGalleryAccessApp}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setUsePhotoGalleryAccessApp(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                    infoMessage={<AppGalleryPermissionTooltip />}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("requiredVolumeConfirmation")}
                    dataCy="requiredVolumeConfirmation"
                    name="requiredVolumeConfirmation"
                    value={requiredVolumeConfirmation}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setRequiredVolumeConfirmation(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
            </Grid>
          </section>
          <section className={classes.section}>
            <p className={classes.sectionTitle}>{translate("shipmentsSettings")}</p>
            <Grid container direction="row" alignItems="flex-start" spacing={2}>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("seaTrackingEnabled")}
                    dataCy="seaTrackingEnabled"
                    name="seaTrackingEnabled"
                    value={seaTrackingEnabled}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setSeaTrackingEnabled(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    label={translate("airTrackingEnabled")}
                    dataCy="airTrackingEnabled"
                    name="airTrackingEnabled"
                    value={airTrackingEnabled}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setIsDefaultValue(false);
                      setAirTrackingEnabled(event.target.value);
                    }}
                    displayEmpty
                    placeholder={translate("selectAnOption")}
                  >
                    <MenuItem data-cy="yes" value={OptionsSwitch.YES}>
                      {translate("yes")}
                    </MenuItem>
                    <MenuItem data-cy="no" value={OptionsSwitch.NO}>
                      {translate("no")}
                    </MenuItem>
                  </Select>
                </SkeletonComponent>
              </Grid>
            </Grid>
          </section>
        </>
      )}
    </Wrapper>
  );
};

export default DefaultPreferences;
